import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * useInfiniteScroll
 *
 * Client-side progressive rendering: instead of rendering every row of a
 * (possibly large) dataset at once, render a growing window of rows and
 * reveal more as the user scrolls to the bottom of the scroll container.
 *
 * This keeps the DOM light while still letting us drive the whole flow
 * from an in-memory array (handy when the backend doesn't paginate).
 *
 * @param {Array} items     Full list of items to progressively render.
 * @param {Object} opts
 * @param {number} opts.pageSize   How many rows to add per "page" (default 30)
 * @param {number} opts.threshold  Pixels from bottom that triggers next page (default 200)
 *
 * @returns {{
 *   visibleItems: Array,
 *   hasMore: boolean,
 *   scrollRef: React.MutableRefObject,
 *   sentinelRef: React.MutableRefObject,
 *   reset: () => void,
 * }}
 */
export default function useInfiniteScroll(items, { pageSize = 30, threshold = 200 } = {}) {
  const [count, setCount] = useState(pageSize);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  // Whenever the source list changes (filter/search/reload), reset the window.
  useEffect(() => {
    setCount(pageSize);
  }, [items, pageSize]);

  const total = items?.length || 0;
  const visibleItems = useMemo(
    () => (items ? items.slice(0, count) : []),
    [items, count]
  );
  const hasMore = count < total;

  const loadMore = useCallback(() => {
    setCount((c) => Math.min(c + pageSize, total));
  }, [pageSize, total]);

  // Scroll listener inside the scroll container.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      if (!hasMore) return;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < threshold) loadMore();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasMore, loadMore, threshold]);

  // Also observe a sentinel row — covers the "container is taller than
  // content so scroll never fires" case.
  useEffect(() => {
    if (!hasMore) return undefined;
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { root, rootMargin: `0px 0px ${threshold}px 0px` }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, threshold, visibleItems.length]);

  const reset = useCallback(() => setCount(pageSize), [pageSize]);

  return { visibleItems, hasMore, scrollRef, sentinelRef, reset };
}
